import React, { useState, useEffect, FormEvent } from "react";
import styled from "styled-components";
import { globalHistory } from "@reach/router";

import { Link, Accordion, IconButton } from "@global";
import {
  assets,
  colors,
  colorsRGB,
  MOBILE_BREAKPOINT,
} from "@util/constants";
import { Container, GridContainer, TextButton } from "@util/standard";
import { isSanityMegaMenuLinks } from "@util/types";
import { navigate } from "gatsby-link";

const Wrapper = styled(Container)`
  flex-direction: column;
  && {
    p {
      color: var(--theme-primary-color);
      font-weight: 600;
      letter-spacing: 0;
    }
  }
  > p {
    padding: 20px 0;
    margin: 0;
    &:not(:first-of-type) {
      border-top: 1px solid var(--theme-primary-color);
    }
  }
`;

const MenuLink = styled(Link)`
  width: 100%;
  padding: 40px 0;
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px){
    padding: 30px 0;
  }
`;

const MenuTextButton = styled(TextButton)`
  color: var(--theme-primary-color);
  width: 100%;
  padding: 40px 0;
  margin: 0;
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px){
    padding: 30px 0;
  }
`;

const ButtonContainer = styled(Container)`
  justify-content: space-between;
  border-bottom: 1.5px solid var(--theme-primary-color);
`;


const SearchContainer = styled(Container)`
  width: 100%;
  position: relative;

  label{
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  }  
  input{
    padding: 10px 45px;
    border: 0;
    border-bottom: 2px solid ${colorsRGB.merlin(0.45)};
    width: 100%;
    position: relative;
    background: ${colors.transparent};
    font-size: 16px;
    box-sizing: border-box;
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  border: 0;
  background: ${colors.transparent};
  padding: 8px 0;
`;

const Form = styled.form`
  width: 100%;
`;

const handleSubmit = (e: FormEvent<HTMLFormElement>, searchTerm: string) => {
  e.preventDefault();

  const searchUrl = `/search?search=${encodeURI(searchTerm)}&all=1`;
  navigate(searchUrl);
};

interface Props {
  handleDrawerClose: () => void;
  menuItems: Queries.Maybe<readonly Queries.Maybe<Queries.SanityLinkOrMegaMenuLinks>[]>;
}

const MobileMenu = ({ handleDrawerClose, menuItems }: Props) => {
  const [showMore, setShowMore] = useState(false);
  const [nestedLinks, setNestedLinks] = useState<
    Queries.Maybe<readonly Queries.Maybe<Queries.SanityNestedLinks>[]> | undefined
  >();
  const [nestedTitle, setNestedTitle] = useState<Queries.Maybe<string> | undefined>();
  const [nestedSlug, setNestedSLug] = useState<Queries.Maybe<string> | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    return globalHistory.listen(({ action }) => {
      if (action === "PUSH") {
        handleDrawerClose();
      }
    });
  }, []);

  const MoreMenu = () => (
    <Wrapper>
      <TextButton
        bold
        color="merlin"
        onClick={() => setShowMore(false)}
        margin="0 0 20px 0"
      >{`< Back`}</TextButton>
      <Link
        isBold
        linktext={nestedTitle}
        url={nestedSlug}
        internallink
        padding="20px 0px"
      />
      {nestedLinks?.map(nestedLink => {
        if (!nestedLink) return null;

        if (nestedLink.title == null) {
          return (
            <GridContainer
              key={nestedLink._key}
              repeat={1}
              tabletRepeat={1}
              rowGap={12}
              margin="20px 0 0 0"
            >
              {nestedLink.links?.map(link => {
                if (link == null) {
                  return null;
                }

                return <Link key={link._key} {...link} />;
              })}
            </GridContainer>
          );
        }

        return (
          <Accordion key={nestedLink._key} title={nestedLink.title as string}>
            <GridContainer
              repeat={1}
              tabletRepeat={1}
              rowGap={12}
              margin="20px 0 0 0"
            >
              {nestedLink.links?.map(link => {
                if (link == null) {
                  return null;
                }

                return <Link key={link._key} {...link} />;
              })}
            </GridContainer>
          </Accordion>
        );
      })}
    </Wrapper>
  );

  const MainMenu = () => (
    <Wrapper>

      {menuItems?.map(menuItemLink => {
        if (menuItemLink == null) {
          return null;
        }

        if (isSanityMegaMenuLinks(menuItemLink)) {
          return (
            <ButtonContainer key={menuItemLink._key}>
              <MenuTextButton
                onClick={() => {
                  setShowMore(true);
                  setNestedLinks(menuItemLink.links);
                  setNestedTitle(menuItemLink.title);
                  setNestedSLug(menuItemLink.slug?.current);
                }}
              >
                {menuItemLink.title}
              </MenuTextButton>
              <img src={assets.arrowRight} height="auto" width="8px" />
            </ButtonContainer>
          );
        }

        return (
          <ButtonContainer key={menuItemLink._key}>
            <MenuLink isBold {...menuItemLink} />
            <img src={assets.arrowRight} height="auto" width="8px" />
          </ButtonContainer>
        );
      })}
    </Wrapper>
  );

  return (
    <Container
      flexDirection="column"
      padding="20px 30px"
      justifyContent="space-between"
      minHeight="89%"
    >
      <Container flexDirection="column">

        <SearchContainer>
          <Form onSubmit={e => handleSubmit(e, searchTerm)}>
            <label htmlFor="searchInput">
              <IconButton
                icon="searchDark"
                alt="Search"
                width="19px"
              />
            </label>
            <input id="searchInput" name="searchInput" type="text" placeholder="Search" value={searchTerm} onChange={e => setSearchTerm(e.currentTarget.value)} />
            <SearchButton type="submit">
              <IconButton
                icon="arrowEnter"
                alt="Search Enter"
                width="15px"
              />
            </SearchButton>
          </Form>
        </SearchContainer>

        {showMore ? <MoreMenu /> : <MainMenu />}
      </Container>
    </Container>
  );
};

export default MobileMenu;
